/**
 * The wall plan of a schema-v2 dungeon (AD-404 §7) — ONE walk over `plan.layout`
 * that the client and the server both read (E4.6.S3 slice 2, AD-698).
 *
 * Moved here from `frontend/src/game/dungeon/layoutWalls.ts` +
 * `interiorCollision.ts` (`q-dungeon-layout-generator-build` S3) so the instance
 * `Zone` collides against the same walls the client predicts against and draws
 * (AD-026 — client prediction runs the server's code; WR.2 / AD-044 — the
 * collider is derived from the LAYOUT, never from render meshes). A dungeon run
 * on the server with bounds-only collision let a body cross a wall the client
 * drew; that was the movement-authority gap slice 1 recorded.
 *
 * Three consumers, one walk: `buildLayoutCollision` (the collider, server and
 * client), and the client renderer, which tiles meshes along `planLayoutWalls`
 * spans. The only difference between the calls is `doorwayJamb`: the collider
 * cuts a doorway at its CLEAR width (the jambs are solid), the renderer at the
 * frame's OUTER width (the frame mesh draws the jambs).
 *
 * Structural input on purpose: `shared` builds first and does not depend on
 * `@great-cloud/procedural-dungeons`; a generated `DungeonPlan` satisfies
 * `LayoutWallInput` as data. Conventions: plan-local metres, x/z; a wall span
 * is its centre-line, the wall is `DUNGEON_WALL_THICKNESS` thick about it, and
 * nothing is extended past a span's ends. No `three` import.
 */
/**
 * Wall thickness of a dungeon interior, metres. The one source: the client's
 * `DUNGEON_INTERIOR_SCALE.wallThickness` reads it, and the package's
 * `DUNGEON_LAYOUT_GLOBALS.doorwayJambWidth` mirrors it (`layoutInterior.test.ts`
 * fails on drift).
 */
export const DUNGEON_WALL_THICKNESS = 0.45;
const WALL = DUNGEON_WALL_THICKNESS;
const HALF_WALL = WALL / 2;
const ON_LINE_EPSILON = 1e-6;
const MIN_SPAN = 1e-6;
function sub(a, b) {
    return { x: a.x - b.x, z: a.z - b.z };
}
function unit(v) {
    const length = Math.hypot(v.x, v.z);
    return { x: v.x / length, z: v.z / length };
}
function offset(p, v, distance) {
    return { x: p.x + v.x * distance, z: p.z + v.z * distance };
}
/** Rotation about +Y that maps local +x onto `along` (the assembler's `yaw` convention). */
export function yawAlong(along) {
    return Math.atan2(-along.z, along.x);
}
/** Parameter of `p` along edge a→b, or null when `p` is not on that edge. */
function onEdge(p, a, u, length) {
    const rel = sub(p, a);
    if (Math.abs(rel.x * u.z - rel.z * u.x) > ON_LINE_EPSILON)
        return null;
    const t = rel.x * u.x + rel.z * u.z;
    return t >= -ON_LINE_EPSILON && t <= length + ON_LINE_EPSILON ? t : null;
}
/** Solid sub-intervals of [0, length] once `cuts` are removed. */
function solidIntervals(length, cuts) {
    const sorted = cuts
        .map(([a, b]) => [Math.max(0, a), Math.min(length, b)])
        .filter(([a, b]) => b > a)
        .sort((left, right) => left[0] - right[0]);
    const solid = [];
    let cursor = 0;
    for (const [a, b] of sorted) {
        if (a - cursor > MIN_SPAN)
            solid.push([cursor, a]);
        cursor = Math.max(cursor, b);
    }
    if (length - cursor > MIN_SPAN)
        solid.push([cursor, length]);
    return solid;
}
function secretFrame(layout, height) {
    const { secret } = layout;
    const [spanA, backA, , spanB] = secret.alcove;
    const outward = unit(sub(backA, spanA));
    // Orient the entrance so local −z (= −(−along.z, along.x)) is the outward side.
    let along = unit(sub(spanB, spanA));
    if (Math.abs(along.z - outward.x) > 1e-9 || Math.abs(-along.x - outward.z) > 1e-9) {
        along = { x: -along.x, z: -along.z };
    }
    const origin = { x: (spanA.x + spanB.x) / 2, z: (spanA.z + spanB.z) / 2 };
    const span = Math.hypot(spanB.x - spanA.x, spanB.z - spanA.z);
    const depth = Math.hypot(backA.x - spanA.x, backA.z - spanA.z);
    // The panel is the part of the span that is not the gap; its centre's side of
    // the origin is the only fact the v1 assembly needs about it.
    const gapLength = Math.hypot(secret.gap.to.x - secret.gap.from.x, secret.gap.to.z - secret.gap.from.z);
    const gapMid = {
        x: (secret.gap.from.x + secret.gap.to.x) / 2,
        z: (secret.gap.from.z + secret.gap.to.z) / 2,
    };
    const gapSide = (gapMid.x - origin.x) * along.x + (gapMid.z - origin.z) * along.z;
    return {
        hostRoomId: secret.hostRoomId,
        originX: origin.x,
        originZ: origin.z,
        yaw: yawAlong(along),
        panelOnNegativeSide: gapSide > 0,
        entranceClearWidth: span,
        panelWidth: span - gapLength,
        alcoveWidth: span + WALL * 2,
        alcoveDepth: depth - WALL,
        height,
    };
}
/**
 * Walk the layout into wall spans, doorways and corridor strips. `doorwayJamb` is
 * the extra cut either side of every opening (0 for collision, the jamb width for
 * the renderer); the secret entrance is cut the same way.
 */
export function planLayoutWalls(plan, options) {
    const { layout } = plan;
    const jamb = options.doorwayJamb;
    const chamberById = new Map(layout.chambers.map((chamber) => [chamber.roomId, chamber]));
    const connectionById = new Map(plan.connections.map((connection) => [connection.id, connection]));
    const openingsByRoom = new Map();
    const addOpening = (roomId, opening) => {
        const list = openingsByRoom.get(roomId) ?? [];
        list.push(opening);
        openingsByRoom.set(roomId, list);
    };
    for (const corridor of layout.corridors) {
        const connection = connectionById.get(corridor.connectionId);
        if (!connection)
            throw new Error(`layout corridor '${corridor.connectionId}' has no connection`);
        const first = corridor.segments[0];
        const last = corridor.segments[corridor.segments.length - 1];
        addOpening(connection.a, {
            connectionId: corridor.connectionId,
            at: first.from,
            clearWidth: first.clearWidth,
        });
        addOpening(connection.b, {
            connectionId: corridor.connectionId,
            at: last.to,
            clearWidth: last.clearWidth,
        });
    }
    const hostHeight = chamberById.get(layout.secret.hostRoomId)?.wallHeight;
    if (hostHeight === undefined) {
        throw new Error(`layout secret host '${layout.secret.hostRoomId}' has no chamber`);
    }
    const secret = secretFrame(layout, hostHeight);
    const secretEnds = [layout.secret.alcove[0], layout.secret.alcove[3]];
    const walls = [];
    const doorways = [];
    for (const chamber of layout.chambers) {
        const openings = openingsByRoom.get(chamber.roomId) ?? [];
        const placed = new Set();
        const outline = chamber.outline;
        for (let index = 0; index < outline.length; index += 1) {
            const a = outline[index];
            const b = outline[(index + 1) % outline.length];
            const length = Math.hypot(b.x - a.x, b.z - a.z);
            if (length <= MIN_SPAN)
                continue;
            const u = unit(sub(b, a));
            const cuts = [];
            for (const opening of openings) {
                if (placed.has(opening))
                    continue;
                const t = onEdge(opening.at, a, u, length);
                if (t === null)
                    continue;
                placed.add(opening);
                cuts.push([t - opening.clearWidth / 2 - jamb, t + opening.clearWidth / 2 + jamb]);
                doorways.push({
                    name: `room:${chamber.roomId}:doorway:${opening.connectionId}`,
                    roomId: chamber.roomId,
                    connectionId: opening.connectionId,
                    at: opening.at,
                    along: u,
                    clearWidth: opening.clearWidth,
                    height: chamber.wallHeight,
                });
            }
            if (chamber.roomId === secret.hostRoomId) {
                const ts = secretEnds.map((end) => onEdge(end, a, u, length));
                if (ts[0] !== null && ts[1] !== null) {
                    cuts.push([Math.min(ts[0], ts[1]) - jamb, Math.max(ts[0], ts[1]) + jamb]);
                }
            }
            for (const [t0, t1] of solidIntervals(length, cuts)) {
                walls.push({
                    name: `room:${chamber.roomId}:wall:${index}:${walls.length}`,
                    from: offset(a, u, t0),
                    to: offset(a, u, t1),
                    height: chamber.wallHeight,
                });
            }
        }
        if (placed.size !== openings.length) {
            throw new Error(`chamber '${chamber.roomId}' has a corridor opening off its outline`);
        }
    }
    const corridors = [];
    for (const corridor of layout.corridors) {
        const connection = connectionById.get(corridor.connectionId);
        const height = Math.min(chamberById.get(connection.a)?.wallHeight ?? Infinity, chamberById.get(connection.b)?.wallHeight ?? Infinity);
        const name = `corridor:${corridor.connectionId}`;
        corridor.segments.forEach((segment, index) => {
            const u = unit(sub(segment.to, segment.from));
            const n = { x: u.z, z: -u.x };
            corridors.push({
                name: `${name}:${index}`,
                from: segment.from,
                to: segment.to,
                clearWidth: segment.clearWidth,
                height,
            });
            for (const sign of [-1, 1]) {
                const lateral = sign * (segment.clearWidth / 2 + WALL / 2);
                walls.push({
                    name: `${name}:${index}:wall:${sign}`,
                    from: offset(segment.from, n, lateral),
                    to: offset(segment.to, n, lateral),
                    height,
                });
            }
            // A width change (the choke's shoulders) leaves a step between the two
            // runs' side walls; close it, or the walker slips out at the corner.
            const next = corridor.segments[index + 1];
            if (next && next.clearWidth !== segment.clearWidth) {
                const inner = Math.min(segment.clearWidth, next.clearWidth) / 2;
                const outer = Math.max(segment.clearWidth, next.clearWidth) / 2 + WALL;
                for (const sign of [-1, 1]) {
                    walls.push({
                        name: `${name}:${index}:shoulder:${sign}`,
                        from: offset(segment.to, n, sign * inner),
                        to: offset(segment.to, n, sign * outer),
                        height,
                    });
                }
            }
        });
    }
    return { walls, doorways, corridors, secret };
}
/**
 * Rotate a local-frame rectangle by `yaw` about Y, translate it to (originX,
 * originZ), and take its world-axis-aligned bounds. Exact for the quarter-turn
 * yaws the assemblers use; an off-axis yaw degrades to a conservative box.
 */
export function placedBox(originX, originZ, yaw, x0, x1, z0, z1) {
    const cos = Math.cos(yaw);
    const sin = Math.sin(yaw);
    let minX = Infinity;
    let maxX = -Infinity;
    let minZ = Infinity;
    let maxZ = -Infinity;
    for (const [lx, lz] of [
        [x0, z0],
        [x1, z0],
        [x0, z1],
        [x1, z1],
    ]) {
        const wx = originX + lx * cos + lz * sin;
        const wz = originZ - lx * sin + lz * cos;
        minX = Math.min(minX, wx);
        maxX = Math.max(maxX, wx);
        minZ = Math.min(minZ, wz);
        maxZ = Math.max(maxZ, wz);
    }
    return { kind: 'aabb', minX, minZ, maxX, maxZ };
}
/**
 * The footprint of one wall span: a wall-thick strip about its centre-line. An
 * axis-aligned span is an exact `aabb`; any other heading (an octagon's diagonal)
 * is a four-point `poly` — the shared resolver treats both alike.
 */
export function wallSpanFootprint(span) {
    const { from, to } = span;
    if (from.x === to.x || from.z === to.z) {
        return {
            kind: 'aabb',
            minX: Math.min(from.x, to.x) - (from.x === to.x ? HALF_WALL : 0),
            maxX: Math.max(from.x, to.x) + (from.x === to.x ? HALF_WALL : 0),
            minZ: Math.min(from.z, to.z) - (from.z === to.z ? HALF_WALL : 0),
            maxZ: Math.max(from.z, to.z) + (from.z === to.z ? HALF_WALL : 0),
        };
    }
    const length = Math.hypot(to.x - from.x, to.z - from.z);
    const nx = ((to.z - from.z) / length) * HALF_WALL;
    const nz = (-(to.x - from.x) / length) * HALF_WALL;
    return {
        kind: 'poly',
        points: [
            { x: from.x + nx, z: from.z + nz },
            { x: to.x + nx, z: to.z + nz },
            { x: to.x - nx, z: to.z - nz },
            { x: from.x - nx, z: from.z - nz },
        ],
    };
}
/**
 * The alcove behind a false wall: two side walls and a back wall, in the secret
 * frame (origin on the host wall line, local −z outward). Without these a
 * player who finds the gap walks straight out of the interior.
 */
export function secretAlcoveShapes(originX, originZ, yaw, alcoveWidth, alcoveDepth) {
    const depth = alcoveDepth;
    const halfWidth = alcoveWidth / 2;
    const nearZ = -(HALF_WALL + depth);
    const farZ = -HALF_WALL;
    return [
        ...[-1, 1].map((sign) => placedBox(originX, originZ, yaw, Math.min(sign * halfWidth, sign * (halfWidth - WALL)), Math.max(sign * halfWidth, sign * (halfWidth - WALL)), nearZ, farZ)),
        placedBox(originX, originZ, yaw, -halfWidth, halfWidth, -(depth + WALL * 1.5), -(depth + WALL * 0.5)),
    ];
}
/**
 * Static collision for a schema-v2 plan (AD-404 §7): every wall span of the wall
 * plan, the secret's false-wall panel, and its alcove. Doorways and the secret
 * entrance are cut at their CLEAR width (jambs solid). Plan-local frame — the
 * client's interior group frame and the server instance zone's world frame are
 * both plan-local, so the same shapes serve both unchanged.
 */
export function buildLayoutCollision(plan) {
    const wallPlan = planLayoutWalls(plan, { doorwayJamb: 0 });
    const shapes = wallPlan.walls.map(wallSpanFootprint);
    const secret = wallPlan.secret;
    const halfSpan = secret.entranceClearWidth / 2;
    const panelFrom = secret.panelOnNegativeSide ? -halfSpan : halfSpan - secret.panelWidth;
    shapes.push(placedBox(secret.originX, secret.originZ, secret.yaw, panelFrom, panelFrom + secret.panelWidth, -HALF_WALL, HALF_WALL), ...secretAlcoveShapes(secret.originX, secret.originZ, secret.yaw, secret.alcoveWidth, secret.alcoveDepth));
    return shapes;
}
//# sourceMappingURL=dungeon-walls.js.map